import {
  Tables,
  TablesInsert,
  TablesUpdate,
  DozenSupabaseClient,
} from "../../database.types";
import { App } from "../types/types";

export type GameRow = Tables<"games">;
export type GameInsert = TablesInsert<"games">;
export type GameUpdate = TablesUpdate<"games">;

type GameWithCreator = App.GameWithCreator;

export type RoundRow = Tables<"rounds">;
export type RoundInsert = TablesInsert<"rounds">;
export type RoundUpdate = TablesUpdate<"rounds">;

export type QuestionRow = Tables<"questions">;
export type QuestionInsert = TablesInsert<"questions">;
export type QuestionUpdate = TablesUpdate<"questions">;

export type QuestionAttachmentRow = Tables<"question_attachments">;
export type QuestionAttachmentInsert = TablesInsert<"question_attachments">;

export type RoundLifelineRow = Tables<"round_lifelines">;
export type RoundLifelineInsert = TablesInsert<"round_lifelines">;

export type GameStateRow = Tables<"game_state">;
export type GameStateInsert = TablesInsert<"game_state">;
export type GameStateUpdate = TablesUpdate<"game_state">;

export type BonusRoundRow = Tables<"bonus_rounds">;
export type BonusRoundInsert = TablesInsert<"bonus_rounds">;
export type BonusRoundUpdate = TablesUpdate<"bonus_rounds">;

export type ClassicBonusAnswerRow = Tables<"classic_bonus_answers">;
export type ClassicBonusAnswerInsert = TablesInsert<"classic_bonus_answers">;
export type ClassicBonusAnswerUpdate = TablesUpdate<"classic_bonus_answers">;

export type ClassicBonusTeamResponseRow = Tables<"classic_bonus_team_responses">;
export type ClassicBonusTeamResponseInsert =
  TablesInsert<"classic_bonus_team_responses">;

export type ClosestGuessQuestionRow = Tables<"closest_guess_questions">;
export type ClosestGuessQuestionInsert = TablesInsert<"closest_guess_questions">;
export type ClosestGuessQuestionUpdate = TablesUpdate<"closest_guess_questions">;

export type QuestionAnswerRow = Tables<"question_answers">;
export type QuestionAnswerInsert = TablesInsert<"question_answers">;

export interface FullGameData {
  game: GameRow;
  gameState: GameStateRow | null;
  rounds: RoundRow[];
  questions: QuestionRow[];
  lifelines: RoundLifelineRow[];
  bonusRounds: BonusRoundRow[];
  classicBonusAnswers: ClassicBonusAnswerRow[];
  classicBonusTeamResponses: ClassicBonusTeamResponseRow[];
  closestGuessQuestions: ClosestGuessQuestionRow[];
}

// ============================================================================
// GAME SERVICE
// ============================================================================

export const gameService = {
  // ==========================================================================
  // GAMES — CRUD & Queries
  // ==========================================================================

  async getAll(supabase: DozenSupabaseClient): Promise<GameRow[]> {
    const { data, error } = await supabase
      .from("games")
      .select("*")
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching games:", error);
      throw error;
    }

    return data || [];
  },

  async getById(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<GameRow | null> {
    const { data, error } = await supabase
      .from("games")
      .select("*")
      .eq("id", id)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error(`Error fetching game with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async listLeagueGames(
    supabase: DozenSupabaseClient,
    leagueId: string,
  ): Promise<GameWithCreator[]> {
    const { data, error } = await supabase
      .from("games")
      .select("*, profiles!games_creator_id_fkey(*)")
      .eq("league_id", leagueId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(`Error fetching games for league ${leagueId}:`, error);
      throw error;
    }

    return (data as unknown as GameWithCreator[]) || [];
  },

  async listMyLeagueGames(
    supabase: DozenSupabaseClient,
    leagueId: string,
    userId: string,
  ): Promise<GameWithCreator[]> {
    const { data, error } = await supabase
      .from("games")
      .select("*, profiles!games_creator_id_fkey(*)")
      .eq("league_id", leagueId)
      .eq("creator_id", userId)
      .is("winning_team_id", null)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(`Error fetching my games for league ${leagueId}:`, error);
      throw error;
    }

    return (data as unknown as GameWithCreator[]) || [];
  },

  async listCompletedLeagueGamesByOthers(
    supabase: DozenSupabaseClient,
    leagueId: string,
  ): Promise<GameWithCreator[]> {
    const { data, error } = await supabase
      .from("games")
      .select("*, profiles!games_creator_id_fkey(*)")
      .eq("league_id", leagueId)
      .not("winning_team_id", "is", null)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(
        `Error fetching completed league games for league ${leagueId}:`,
        error,
      );
      throw error;
    }

    return (data as unknown as GameWithCreator[]) || [];
  },

  async create(
    supabase: DozenSupabaseClient,
    game: GameInsert,
  ): Promise<GameRow> {
    const { data, error } = await supabase
      .from("games")
      .insert(game)
      .select()
      .single();

    if (error) {
      console.error("Error creating game:", error);
      throw error;
    }

    return data;
  },

  async update(
    supabase: DozenSupabaseClient,
    id: string,
    updates: GameUpdate,
  ): Promise<GameRow> {
    const { data, error } = await supabase
      .from("games")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating game with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  /** Soft-delete a game by setting deleted_at. */
  async softDelete(supabase: DozenSupabaseClient, id: string): Promise<void> {
    const { error } = await supabase
      .from("games")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error(`Error soft-deleting game with id ${id}:`, error);
      throw error;
    }
  },

  /** Hard-delete a game. Use with caution. */
  async delete(supabase: DozenSupabaseClient, id: string): Promise<void> {
    const { error } = await supabase.from("games").delete().eq("id", id);

    if (error) {
      console.error(`Error deleting game with id ${id}:`, error);
      throw error;
    }
  },

  /** Mark a game as started. */
  async startGame(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<GameRow> {
    return gameService.update(supabase, id, { is_started: true });
  },

  /** Mark a game as played/completed. */
  async markPlayed(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<GameRow> {
    return gameService.update(supabase, id, { played: true });
  },

  /** Set (or clear) the winning team. Pass null to undeclare. */
  async setWinner(
    supabase: DozenSupabaseClient,
    gameId: string,
    winningTeamId: string | null,
  ): Promise<GameRow> {
    return gameService.update(supabase, gameId, {
      winning_team_id: winningTeamId,
    });
  },

  /** Update team selections for a game. */
  async setTeams(
    supabase: DozenSupabaseClient,
    gameId: string,
    teamOneId: string | null,
    teamTwoId: string | null,
  ): Promise<GameRow> {
    return gameService.update(supabase, gameId, {
      team_one_id: teamOneId,
      team_two_id: teamTwoId,
    });
  },

  /** Update niche categories for a game. */
  async setNicheCategories(
    supabase: DozenSupabaseClient,
    gameId: string,
    team1Niche: string | null,
    team2Niche: string | null,
  ): Promise<GameRow> {
    return gameService.update(supabase, gameId, {
      team_1_niche_category: team1Niche,
      team_2_niche_category: team2Niche,
    });
  },

  /** Mark the game config as complete or incomplete. */
  async setConfigComplete(
    supabase: DozenSupabaseClient,
    gameId: string,
    complete: boolean,
  ): Promise<GameRow> {
    return gameService.update(supabase, gameId, { config_complete: complete });
  },

  // ==========================================================================
  // GAME STATE — Live game control state
  // ==========================================================================

  async getGameState(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<GameStateRow | null> {
    const { data, error } = await supabase
      .from("game_state")
      .select("*")
      .eq("game_id", gameId)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null; // no rows
      console.error(`Error fetching game state for game ${gameId}:`, error);
      throw error;
    }

    return data;
  },

  async createGameState(
    supabase: DozenSupabaseClient,
    gameState: GameStateInsert,
  ): Promise<GameStateRow> {
    const { data, error } = await supabase
      .from("game_state")
      .insert(gameState)
      .select()
      .single();

    if (error) {
      console.error("Error creating game state:", error);
      throw error;
    }

    return data;
  },

  async updateGameState(
    supabase: DozenSupabaseClient,
    gameId: string,
    updates: GameStateUpdate,
  ): Promise<GameStateRow> {
    const { data, error } = await supabase
      .from("game_state")
      .update(updates)
      .eq("game_id", gameId)
      .select()
      .single();

    if (error) {
      console.error(
        `Error updating game state for game ${gameId}:`,
        error,
      );
      throw error;
    }

    return data;
  },

  async upsertGameState(
    supabase: DozenSupabaseClient,
    gameState: GameStateInsert,
  ): Promise<GameStateRow> {
    const { data, error } = await supabase
      .from("game_state")
      .upsert(gameState, { onConflict: "game_id" })
      .select()
      .single();

    if (error) {
      console.error("Error upserting game state:", error);
      throw error;
    }

    return data;
  },

  /** Navigate to a specific round number. Resets question/answer visibility. */
  async navigateToRound(
    supabase: DozenSupabaseClient,
    gameId: string,
    roundNumber: number | null,
    phase: string = "standard",
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      current_round_number: roundNumber,
      current_phase: phase,
      current_question_num: 1,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });
  },

  /** Navigate to bonus round phase. */
  async navigateToBonusRound(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      current_round_number: null,
      current_phase: "bonus",
      current_question_num: 1,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });
  },

  /** Navigate to overtime phase. */
  async navigateToOvertime(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      current_round_number: null,
      current_phase: "overtime",
      current_question_num: 1,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });
  },

  /** Switch to a different question number (1 or 2). */
  async switchQuestion(
    supabase: DozenSupabaseClient,
    gameId: string,
    questionNum: number,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      current_question_num: questionNum,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });
  },

  /** Toggle question visibility. */
  async toggleShowQuestion(
    supabase: DozenSupabaseClient,
    gameId: string,
    show: boolean,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      show_question: show,
    });
  },

  /** Toggle answer visibility. */
  async toggleShowAnswer(
    supabase: DozenSupabaseClient,
    gameId: string,
    show: boolean,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      show_answer: show,
    });
  },

  /** Set the steal flag. */
  async setSteal(
    supabase: DozenSupabaseClient,
    gameId: string,
    isSteal: boolean,
  ): Promise<GameStateRow> {
    return gameService.updateGameState(supabase, gameId, {
      is_steal: isSteal,
    });
  },

  // ==========================================================================
  // ROUNDS — Per-game rounds (always 12 standard rounds)
  // ==========================================================================

  async getGameRounds(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<RoundRow[]> {
    const { data, error } = await supabase
      .from("rounds")
      .select("*")
      .eq("game_id", gameId)
      .order("round_number", { ascending: true });

    if (error) {
      console.error(`Error fetching rounds for game ${gameId}:`, error);
      throw error;
    }

    return data || [];
  },

  async getRoundById(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<RoundRow | null> {
    const { data, error } = await supabase
      .from("rounds")
      .select("*")
      .eq("id", roundId)
      .single();

    if (error) {
      console.error(`Error fetching round ${roundId}:`, error);
      throw error;
    }

    return data;
  },

  async createRound(
    supabase: DozenSupabaseClient,
    round: RoundInsert,
  ): Promise<RoundRow> {
    const { data, error } = await supabase
      .from("rounds")
      .insert(round)
      .select()
      .single();

    if (error) {
      console.error("Error creating round:", error);
      throw error;
    }

    return data;
  },

  /** Bulk-create all 12 rounds for a game at once. */
  async createAllRounds(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<RoundRow[]> {
    const rounds: RoundInsert[] = Array.from({ length: 12 }, (_, i) => ({
      game_id: gameId,
      round_number: i + 1,
      category: "",
    }));

    const { data, error } = await supabase
      .from("rounds")
      .insert(rounds)
      .select();

    if (error) {
      console.error(`Error creating rounds for game ${gameId}:`, error);
      throw error;
    }

    return data || [];
  },

  async updateRound(
    supabase: DozenSupabaseClient,
    roundId: string,
    updates: RoundUpdate,
  ): Promise<RoundRow> {
    const { data, error } = await supabase
      .from("rounds")
      .update(updates)
      .eq("id", roundId)
      .select()
      .single();

    if (error) {
      console.error(`Error updating round ${roundId}:`, error);
      throw error;
    }

    return data;
  },

  /** Update the category for a specific round. */
  async setRoundCategory(
    supabase: DozenSupabaseClient,
    roundId: string,
    category: string,
  ): Promise<RoundRow> {
    return gameService.updateRound(supabase, roundId, { category });
  },

  async deleteRound(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<void> {
    const { error } = await supabase.from("rounds").delete().eq("id", roundId);

    if (error) {
      console.error(`Error deleting round ${roundId}:`, error);
      throw error;
    }
  },

  // ==========================================================================
  // QUESTIONS — Per-round questions (2 per round)
  // ==========================================================================

  async getRoundQuestions(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<QuestionRow[]> {
    const { data, error } = await supabase
      .from("questions")
      .select("*")
      .eq("round_id", roundId)
      .order("question_number", { ascending: true });

    if (error) {
      console.error(`Error fetching questions for round ${roundId}:`, error);
      throw error;
    }

    return data || [];
  },

  async getQuestionById(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<QuestionRow | null> {
    const { data, error } = await supabase
      .from("questions")
      .select("*")
      .eq("id", questionId)
      .single();

    if (error) {
      console.error(`Error fetching question ${questionId}:`, error);
      throw error;
    }

    return data;
  },

  /** Get all questions for a game (across all rounds), ordered by round then question number. */
  async getGameQuestions(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<QuestionRow[]> {
    const rounds = await gameService.getGameRounds(supabase, gameId);
    if (rounds.length === 0) return [];

    const roundIds = rounds.map((r) => r.id);
    const { data, error } = await supabase
      .from("questions")
      .select("*")
      .in("round_id", roundIds)
      .order("question_number", { ascending: true });

    if (error) {
      console.error(`Error fetching questions for game ${gameId}:`, error);
      throw error;
    }

    return data || [];
  },

  async createQuestion(
    supabase: DozenSupabaseClient,
    question: QuestionInsert,
  ): Promise<QuestionRow> {
    const { data, error } = await supabase
      .from("questions")
      .insert(question)
      .select()
      .single();

    if (error) {
      console.error("Error creating question:", error);
      throw error;
    }

    return data;
  },

  /** Create both questions for a round at once. */
  async createRoundQuestions(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<QuestionRow[]> {
    const questions: QuestionInsert[] = [
      { round_id: roundId, question_number: 1, question_text: "", answer_text: "" },
      { round_id: roundId, question_number: 2, question_text: "", answer_text: "" },
    ];

    const { data, error } = await supabase
      .from("questions")
      .insert(questions)
      .select();

    if (error) {
      console.error(
        `Error creating questions for round ${roundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async updateQuestion(
    supabase: DozenSupabaseClient,
    questionId: string,
    updates: QuestionUpdate,
  ): Promise<QuestionRow> {
    const { data, error } = await supabase
      .from("questions")
      .update(updates)
      .eq("id", questionId)
      .select()
      .single();

    if (error) {
      console.error(`Error updating question ${questionId}:`, error);
      throw error;
    }

    return data;
  },

  /** Set the question text for a question. */
  async setQuestionText(
    supabase: DozenSupabaseClient,
    questionId: string,
    questionText: string,
  ): Promise<QuestionRow> {
    return gameService.updateQuestion(supabase, questionId, {
      question_text: questionText,
    });
  },

  /** Set the answer text for a question. */
  async setAnswerText(
    supabase: DozenSupabaseClient,
    questionId: string,
    answerText: string,
  ): Promise<QuestionRow> {
    return gameService.updateQuestion(supabase, questionId, {
      answer_text: answerText,
    });
  },

  /** Award a point to a team for a question. */
  async awardPoint(
    supabase: DozenSupabaseClient,
    questionId: string,
    teamId: string,
  ): Promise<QuestionRow> {
    return gameService.updateQuestion(supabase, questionId, {
      correct_team_id: teamId,
    });
  },

  /** Remove a point from a question (un-award). */
  async unawardPoint(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<QuestionRow> {
    return gameService.updateQuestion(supabase, questionId, {
      correct_team_id: null,
    });
  },

  async deleteQuestion(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("questions")
      .delete()
      .eq("id", questionId);

    if (error) {
      console.error(`Error deleting question ${questionId}:`, error);
      throw error;
    }
  },

  // ==========================================================================
  // QUESTION ATTACHMENTS — Images/media per question
  // ==========================================================================

  async getQuestionAttachments(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<QuestionAttachmentRow[]> {
    const { data, error } = await supabase
      .from("question_attachments")
      .select("*")
      .eq("question_id", questionId)
      .order("sort_order", { ascending: true });

    if (error) {
      console.error(
        `Error fetching attachments for question ${questionId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async addQuestionAttachment(
    supabase: DozenSupabaseClient,
    attachment: QuestionAttachmentInsert,
  ): Promise<QuestionAttachmentRow> {
    const { data, error } = await supabase
      .from("question_attachments")
      .insert(attachment)
      .select()
      .single();

    if (error) {
      console.error("Error adding question attachment:", error);
      throw error;
    }

    return data;
  },

  async removeQuestionAttachment(
    supabase: DozenSupabaseClient,
    attachmentId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("question_attachments")
      .delete()
      .eq("id", attachmentId);

    if (error) {
      console.error(`Error removing attachment ${attachmentId}:`, error);
      throw error;
    }
  },

  /** Remove all attachments for a question. */
  async clearQuestionAttachments(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("question_attachments")
      .delete()
      .eq("question_id", questionId);

    if (error) {
      console.error(
        `Error clearing attachments for question ${questionId}:`,
        error,
      );
      throw error;
    }
  },

  // ==========================================================================
  // ROUND LIFELINES — Lifeline usage per round per team
  // ==========================================================================

  async getRoundLifelines(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<RoundLifelineRow[]> {
    const { data, error } = await supabase
      .from("round_lifelines")
      .select("*")
      .eq("round_id", roundId);

    if (error) {
      console.error(
        `Error fetching lifelines for round ${roundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  /** Get all lifelines used across all rounds of a game. */
  async getGameLifelines(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<RoundLifelineRow[]> {
    const rounds = await gameService.getGameRounds(supabase, gameId);
    if (rounds.length === 0) return [];

    const roundIds = rounds.map((r) => r.id);
    const { data, error } = await supabase
      .from("round_lifelines")
      .select("*")
      .in("round_id", roundIds);

    if (error) {
      console.error(`Error fetching lifelines for game ${gameId}:`, error);
      throw error;
    }

    return data || [];
  },

  /** Get lifelines used by a specific team across the entire game. */
  async getTeamLifelines(
    supabase: DozenSupabaseClient,
    gameId: string,
    teamId: string,
  ): Promise<RoundLifelineRow[]> {
    const rounds = await gameService.getGameRounds(supabase, gameId);
    if (rounds.length === 0) return [];

    const roundIds = rounds.map((r) => r.id);
    const { data, error } = await supabase
      .from("round_lifelines")
      .select("*")
      .in("round_id", roundIds)
      .eq("team_id", teamId);

    if (error) {
      console.error(
        `Error fetching lifelines for team ${teamId} in game ${gameId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async addLifeline(
    supabase: DozenSupabaseClient,
    lifeline: RoundLifelineInsert,
  ): Promise<RoundLifelineRow> {
    const { data, error } = await supabase
      .from("round_lifelines")
      .insert(lifeline)
      .select()
      .single();

    if (error) {
      console.error("Error adding lifeline:", error);
      throw error;
    }

    return data;
  },

  async removeLifeline(
    supabase: DozenSupabaseClient,
    lifelineId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("round_lifelines")
      .delete()
      .eq("id", lifelineId);

    if (error) {
      console.error(`Error removing lifeline ${lifelineId}:`, error);
      throw error;
    }
  },

  /** Remove a lifeline by round, team, and type (for toggling). */
  async removeLifelineByType(
    supabase: DozenSupabaseClient,
    roundId: string,
    teamId: string,
    lifelineType: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("round_lifelines")
      .delete()
      .eq("round_id", roundId)
      .eq("team_id", teamId)
      .eq("lifeline_type", lifelineType);

    if (error) {
      console.error(
        `Error removing lifeline ${lifelineType} for team ${teamId} in round ${roundId}:`,
        error,
      );
      throw error;
    }
  },

  /** Toggle a lifeline: add if not present, remove if already used. */
  async toggleLifeline(
    supabase: DozenSupabaseClient,
    roundId: string,
    teamId: string,
    lifelineType: string,
    usedByProfileId?: string | null,
  ): Promise<{ added: boolean }> {
    const existing = await supabase
      .from("round_lifelines")
      .select("id")
      .eq("round_id", roundId)
      .eq("team_id", teamId)
      .eq("lifeline_type", lifelineType);

    if (existing.error) {
      throw existing.error;
    }

    if (existing.data && existing.data.length > 0) {
      await gameService.removeLifeline(supabase, existing.data[0].id);
      return { added: false };
    } else {
      await gameService.addLifeline(supabase, {
        round_id: roundId,
        team_id: teamId,
        lifeline_type: lifelineType,
        used_by_profile_id: usedByProfileId ?? null,
      });
      return { added: true };
    }
  },

  // ==========================================================================
  // BONUS ROUNDS — Bonus round config and live state
  // ==========================================================================

  async getBonusRound(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<BonusRoundRow | null> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .select("*")
      .eq("id", bonusRoundId)
      .single();

    if (error) {
      console.error(`Error fetching bonus round ${bonusRoundId}:`, error);
      throw error;
    }

    return data;
  },

  /** Get all bonus rounds for a game (typically one main + one overtime). */
  async getGameBonusRounds(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<BonusRoundRow[]> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .select("*")
      .eq("game_id", gameId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error(`Error fetching bonus rounds for game ${gameId}:`, error);
      throw error;
    }

    return data || [];
  },

  /** Get the main bonus round for a game (phase = 'bonus'). */
  async getMainBonusRound(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<BonusRoundRow | null> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .select("*")
      .eq("game_id", gameId)
      .eq("phase", "bonus")
      .single();

    if (error) {
      if (error.code === "PGRST116") return null;
      console.error(
        `Error fetching main bonus round for game ${gameId}:`,
        error,
      );
      throw error;
    }

    return data;
  },

  /** Get the overtime bonus round for a game (phase = 'overtime'). */
  async getOvertimeBonusRound(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<BonusRoundRow | null> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .select("*")
      .eq("game_id", gameId)
      .eq("phase", "overtime")
      .single();

    if (error) {
      if (error.code === "PGRST116") return null;
      console.error(
        `Error fetching overtime bonus round for game ${gameId}:`,
        error,
      );
      throw error;
    }

    return data;
  },

  async createBonusRound(
    supabase: DozenSupabaseClient,
    bonusRound: BonusRoundInsert,
  ): Promise<BonusRoundRow> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .insert(bonusRound)
      .select()
      .single();

    if (error) {
      console.error("Error creating bonus round:", error);
      throw error;
    }

    return data;
  },

  async updateBonusRound(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    updates: BonusRoundUpdate,
  ): Promise<BonusRoundRow> {
    const { data, error } = await supabase
      .from("bonus_rounds")
      .update(updates)
      .eq("id", bonusRoundId)
      .select()
      .single();

    if (error) {
      console.error(`Error updating bonus round ${bonusRoundId}:`, error);
      throw error;
    }

    return data;
  },

  /** Toggle the show_question flag on a bonus round. */
  async toggleBonusShowQuestion(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    show: boolean,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      show_question: show,
    });
  },

  /** Toggle the show_answer flag on a bonus round. */
  async toggleBonusShowAnswer(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    show: boolean,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      show_answer: show,
    });
  },

  /** Toggle reveal-all for classic bonus rounds. */
  async toggleBonusRevealAll(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    reveal: boolean,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      is_reveal_all: reveal,
    });
  },

  /** Set the current question index for closest guess bonus rounds. */
  async setBonusCurrentQuestionIdx(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    idx: number | null,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      current_question_idx: idx,
    });
  },

  /** Set the bonus round question text (for Classic type). */
  async setBonusQuestionText(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    questionText: string,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      question_text: questionText,
    });
  },

  /** Set the winning team for a bonus round. */
  async setBonusWinner(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    winningTeamId: string | null,
  ): Promise<BonusRoundRow> {
    return gameService.updateBonusRound(supabase, bonusRoundId, {
      winning_team_id: winningTeamId,
    });
  },

  async deleteBonusRound(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("bonus_rounds")
      .delete()
      .eq("id", bonusRoundId);

    if (error) {
      console.error(`Error deleting bonus round ${bonusRoundId}:`, error);
      throw error;
    }
  },

  // ==========================================================================
  // CLASSIC BONUS ANSWERS — Answer list for Classic (List) bonus rounds
  // ==========================================================================

  async getClassicBonusAnswers(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<ClassicBonusAnswerRow[]> {
    const { data, error } = await supabase
      .from("classic_bonus_answers")
      .select("*")
      .eq("bonus_round_id", bonusRoundId)
      .order("sort_order", { ascending: true });

    if (error) {
      console.error(
        `Error fetching classic bonus answers for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async createClassicBonusAnswer(
    supabase: DozenSupabaseClient,
    answer: ClassicBonusAnswerInsert,
  ): Promise<ClassicBonusAnswerRow> {
    const { data, error } = await supabase
      .from("classic_bonus_answers")
      .insert(answer)
      .select()
      .single();

    if (error) {
      console.error("Error creating classic bonus answer:", error);
      throw error;
    }

    return data;
  },

  /** Bulk-create classic bonus answers. */
  async createClassicBonusAnswers(
    supabase: DozenSupabaseClient,
    answers: ClassicBonusAnswerInsert[],
  ): Promise<ClassicBonusAnswerRow[]> {
    const { data, error } = await supabase
      .from("classic_bonus_answers")
      .insert(answers)
      .select();

    if (error) {
      console.error("Error bulk-creating classic bonus answers:", error);
      throw error;
    }

    return data || [];
  },

  async updateClassicBonusAnswer(
    supabase: DozenSupabaseClient,
    answerId: string,
    updates: ClassicBonusAnswerUpdate,
  ): Promise<ClassicBonusAnswerRow> {
    const { data, error } = await supabase
      .from("classic_bonus_answers")
      .update(updates)
      .eq("id", answerId)
      .select()
      .single();

    if (error) {
      console.error(`Error updating classic bonus answer ${answerId}:`, error);
      throw error;
    }

    return data;
  },

  /** Mark a classic bonus answer as correctly guessed by a team. */
  async markClassicAnswerCorrect(
    supabase: DozenSupabaseClient,
    answerId: string,
    teamId: string,
  ): Promise<ClassicBonusAnswerRow> {
    return gameService.updateClassicBonusAnswer(supabase, answerId, {
      correct_team_id: teamId,
    });
  },

  /** Clear the correct_team_id from a classic bonus answer (undo). */
  async unmarkClassicAnswerCorrect(
    supabase: DozenSupabaseClient,
    answerId: string,
  ): Promise<ClassicBonusAnswerRow> {
    return gameService.updateClassicBonusAnswer(supabase, answerId, {
      correct_team_id: null,
    });
  },

  async deleteClassicBonusAnswer(
    supabase: DozenSupabaseClient,
    answerId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("classic_bonus_answers")
      .delete()
      .eq("id", answerId);

    if (error) {
      console.error(`Error deleting classic bonus answer ${answerId}:`, error);
      throw error;
    }
  },

  /** Delete all classic bonus answers for a bonus round. */
  async clearClassicBonusAnswers(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("classic_bonus_answers")
      .delete()
      .eq("bonus_round_id", bonusRoundId);

    if (error) {
      console.error(
        `Error clearing classic bonus answers for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }
  },

  /** Replace all classic bonus answers for a bonus round (delete + insert). */
  async replaceClassicBonusAnswers(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    answers: Omit<ClassicBonusAnswerInsert, "bonus_round_id">[],
  ): Promise<ClassicBonusAnswerRow[]> {
    await gameService.clearClassicBonusAnswers(supabase, bonusRoundId);
    const inserts = answers.map((a, idx) => ({
      ...a,
      bonus_round_id: bonusRoundId,
      sort_order: idx,
    }));
    return gameService.createClassicBonusAnswers(supabase, inserts);
  },

  // ==========================================================================
  // CLASSIC BONUS TEAM RESPONSES — Team answers during classic bonus gameplay
  // ==========================================================================

  async getClassicBonusTeamResponses(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<ClassicBonusTeamResponseRow[]> {
    const { data, error } = await supabase
      .from("classic_bonus_team_responses")
      .select("*")
      .eq("bonus_round_id", bonusRoundId)
      .order("response_order", { ascending: true });

    if (error) {
      console.error(
        `Error fetching team responses for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  /** Get team responses filtered by team. */
  async getClassicBonusTeamResponsesByTeam(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    teamId: string,
  ): Promise<ClassicBonusTeamResponseRow[]> {
    const { data, error } = await supabase
      .from("classic_bonus_team_responses")
      .select("*")
      .eq("bonus_round_id", bonusRoundId)
      .eq("team_id", teamId)
      .order("response_order", { ascending: true });

    if (error) {
      console.error(
        `Error fetching team ${teamId} responses for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async addClassicBonusTeamResponse(
    supabase: DozenSupabaseClient,
    response: ClassicBonusTeamResponseInsert,
  ): Promise<ClassicBonusTeamResponseRow> {
    const { data, error } = await supabase
      .from("classic_bonus_team_responses")
      .insert(response)
      .select()
      .single();

    if (error) {
      console.error("Error adding classic bonus team response:", error);
      throw error;
    }

    return data;
  },

  /** Remove the last team response (undo action). */
  async removeLastClassicBonusTeamResponse(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    teamId: string,
  ): Promise<void> {
    const responses = await gameService.getClassicBonusTeamResponsesByTeam(
      supabase,
      bonusRoundId,
      teamId,
    );

    if (responses.length > 0) {
      const lastResponse = responses[responses.length - 1];
      const { error } = await supabase
        .from("classic_bonus_team_responses")
        .delete()
        .eq("id", lastResponse.id);

      if (error) {
        console.error(
          `Error removing last team response for team ${teamId}:`,
          error,
        );
        throw error;
      }
    }
  },

  /** Clear all team responses for a bonus round. */
  async clearClassicBonusTeamResponses(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("classic_bonus_team_responses")
      .delete()
      .eq("bonus_round_id", bonusRoundId);

    if (error) {
      console.error(
        `Error clearing team responses for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }
  },

  // ==========================================================================
  // CLOSEST GUESS QUESTIONS — Questions for Closest Guess Battle bonus rounds
  // ==========================================================================

  async getClosestGuessQuestions(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<ClosestGuessQuestionRow[]> {
    const { data, error } = await supabase
      .from("closest_guess_questions")
      .select("*")
      .eq("bonus_round_id", bonusRoundId)
      .order("question_order", { ascending: true });

    if (error) {
      console.error(
        `Error fetching closest guess questions for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  async getClosestGuessQuestionById(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<ClosestGuessQuestionRow | null> {
    const { data, error } = await supabase
      .from("closest_guess_questions")
      .select("*")
      .eq("id", questionId)
      .single();

    if (error) {
      console.error(`Error fetching closest guess question ${questionId}:`, error);
      throw error;
    }

    return data;
  },

  async createClosestGuessQuestion(
    supabase: DozenSupabaseClient,
    question: ClosestGuessQuestionInsert,
  ): Promise<ClosestGuessQuestionRow> {
    const { data, error } = await supabase
      .from("closest_guess_questions")
      .insert(question)
      .select()
      .single();

    if (error) {
      console.error("Error creating closest guess question:", error);
      throw error;
    }

    return data;
  },

  /** Bulk-create closest guess questions. */
  async createClosestGuessQuestions(
    supabase: DozenSupabaseClient,
    questions: ClosestGuessQuestionInsert[],
  ): Promise<ClosestGuessQuestionRow[]> {
    const { data, error } = await supabase
      .from("closest_guess_questions")
      .insert(questions)
      .select();

    if (error) {
      console.error("Error bulk-creating closest guess questions:", error);
      throw error;
    }

    return data || [];
  },

  async updateClosestGuessQuestion(
    supabase: DozenSupabaseClient,
    questionId: string,
    updates: ClosestGuessQuestionUpdate,
  ): Promise<ClosestGuessQuestionRow> {
    const { data, error } = await supabase
      .from("closest_guess_questions")
      .update(updates)
      .eq("id", questionId)
      .select()
      .single();

    if (error) {
      console.error(
        `Error updating closest guess question ${questionId}:`,
        error,
      );
      throw error;
    }

    return data;
  },

  /** Set a team's answer for a closest guess question. */
  async setClosestGuessTeamAnswer(
    supabase: DozenSupabaseClient,
    questionId: string,
    team: 1 | 2,
    answer: number | null,
  ): Promise<ClosestGuessQuestionRow> {
    const updates: ClosestGuessQuestionUpdate =
      team === 1 ? { team_1_answer: answer } : { team_2_answer: answer };
    return gameService.updateClosestGuessQuestion(
      supabase,
      questionId,
      updates,
    );
  },

  /** Set the correct answer for a closest guess question. */
  async setClosestGuessCorrectAnswer(
    supabase: DozenSupabaseClient,
    questionId: string,
    correctAnswer: number | null,
  ): Promise<ClosestGuessQuestionRow> {
    return gameService.updateClosestGuessQuestion(supabase, questionId, {
      correct_answer: correctAnswer,
    });
  },

  async deleteClosestGuessQuestion(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("closest_guess_questions")
      .delete()
      .eq("id", questionId);

    if (error) {
      console.error(
        `Error deleting closest guess question ${questionId}:`,
        error,
      );
      throw error;
    }
  },

  /** Delete all closest guess questions for a bonus round. */
  async clearClosestGuessQuestions(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("closest_guess_questions")
      .delete()
      .eq("bonus_round_id", bonusRoundId);

    if (error) {
      console.error(
        `Error clearing closest guess questions for bonus round ${bonusRoundId}:`,
        error,
      );
      throw error;
    }
  },

  /** Replace all closest guess questions for a bonus round (delete + insert). */
  async replaceClosestGuessQuestions(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
    questions: Omit<ClosestGuessQuestionInsert, "bonus_round_id">[],
  ): Promise<ClosestGuessQuestionRow[]> {
    await gameService.clearClosestGuessQuestions(supabase, bonusRoundId);
    const inserts = questions.map((q, idx) => ({
      ...q,
      bonus_round_id: bonusRoundId,
      question_order: idx,
    }));
    return gameService.createClosestGuessQuestions(supabase, inserts);
  },

  // ==========================================================================
  // COMPOSITE OPERATIONS — Higher-level operations spanning multiple tables
  // ==========================================================================

  /**
   * Initialize a full game with all its child records:
   * 12 rounds (each with 2 questions), a game_state, and a bonus round.
   */
  async initializeFullGame(
    supabase: DozenSupabaseClient,
    gameId: string,
    bonusRoundType: string = "Classic (List)",
  ): Promise<{
    rounds: RoundRow[];
    questions: QuestionRow[];
    gameState: GameStateRow;
    bonusRound: BonusRoundRow;
  }> {
    // Create 12 rounds
    const rounds = await gameService.createAllRounds(supabase, gameId);

    // Create 2 questions per round
    const allQuestions: QuestionRow[] = [];
    for (const round of rounds) {
      const questions = await gameService.createRoundQuestions(
        supabase,
        round.id,
      );
      allQuestions.push(...questions);
    }

    // Create game state
    const gameState = await gameService.createGameState(supabase, {
      game_id: gameId,
      current_phase: "not_started",
      current_question_num: 1,
      current_round_number: null,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });

    // Create main bonus round
    const bonusRound = await gameService.createBonusRound(supabase, {
      game_id: gameId,
      type: bonusRoundType,
      phase: "bonus",
    });

    return { rounds, questions: allQuestions, gameState, bonusRound };
  },

  /**
   * Fetch all game data in one call: game, game_state, rounds with questions,
   * lifelines, bonus rounds with their answers/questions.
   */
  async getFullGame(
    supabase: DozenSupabaseClient,
    gameId: string,
  ): Promise<{
    game: GameRow;
    gameState: GameStateRow | null;
    rounds: RoundRow[];
    questions: QuestionRow[];
    lifelines: RoundLifelineRow[];
    bonusRounds: BonusRoundRow[];
    classicBonusAnswers: ClassicBonusAnswerRow[];
    classicBonusTeamResponses: ClassicBonusTeamResponseRow[];
    closestGuessQuestions: ClosestGuessQuestionRow[];
    questionAnswers: QuestionAnswerRow[];
  } | null> {
    const game = await gameService.getById(supabase, gameId);
    if (!game) return null;

    const [gameState, rounds, bonusRounds] = await Promise.all([
      gameService.getGameState(supabase, gameId),
      gameService.getGameRounds(supabase, gameId),
      gameService.getGameBonusRounds(supabase, gameId),
    ]);

    const roundIds = rounds.map((r) => r.id);
    const bonusRoundIds = bonusRounds.map((br) => br.id);

    // Fetch questions, lifelines, and bonus round child data in parallel
    const [
      questionsResult,
      lifelinesResult,
      classicAnswersResult,
      classicResponsesResult,
      closestGuessResult,
    ] = await Promise.all([
      roundIds.length > 0
        ? supabase
            .from("questions")
            .select("*")
            .in("round_id", roundIds)
            .order("question_number", { ascending: true })
        : { data: [], error: null },
      roundIds.length > 0
        ? supabase
            .from("round_lifelines")
            .select("*")
            .in("round_id", roundIds)
        : { data: [], error: null },
      bonusRoundIds.length > 0
        ? supabase
            .from("classic_bonus_answers")
            .select("*")
            .in("bonus_round_id", bonusRoundIds)
            .order("sort_order", { ascending: true })
        : { data: [], error: null },
      bonusRoundIds.length > 0
        ? supabase
            .from("classic_bonus_team_responses")
            .select("*")
            .in("bonus_round_id", bonusRoundIds)
            .order("response_order", { ascending: true })
        : { data: [], error: null },
      bonusRoundIds.length > 0
        ? supabase
            .from("closest_guess_questions")
            .select("*")
            .in("bonus_round_id", bonusRoundIds)
            .order("question_order", { ascending: true })
        : { data: [], error: null },
    ]);

    if (questionsResult.error) throw questionsResult.error;
    if (lifelinesResult.error) throw lifelinesResult.error;
    if (classicAnswersResult.error) throw classicAnswersResult.error;
    if (classicResponsesResult.error) throw classicResponsesResult.error;
    if (closestGuessResult.error) throw closestGuessResult.error;

    const questions = questionsResult.data || [];
    const questionIds = questions.map((q) => q.id);
    const questionAnswers =
      questionIds.length > 0
        ? await gameService.getQuestionAnswersByQuestionIds(
            supabase,
            questionIds,
          )
        : [];

    return {
      game,
      gameState,
      rounds,
      questions,
      lifelines: lifelinesResult.data || [],
      bonusRounds,
      classicBonusAnswers: classicAnswersResult.data || [],
      classicBonusTeamResponses: classicResponsesResult.data || [],
      closestGuessQuestions: closestGuessResult.data || [],
      questionAnswers,
    };
  },

  /**
   * Get a round with its questions and lifelines in one call.
   */
  async getRoundWithDetails(
    supabase: DozenSupabaseClient,
    roundId: string,
  ): Promise<{
    round: RoundRow;
    questions: QuestionRow[];
    lifelines: RoundLifelineRow[];
  } | null> {
    const round = await gameService.getRoundById(supabase, roundId);
    if (!round) return null;

    const [questions, lifelines] = await Promise.all([
      gameService.getRoundQuestions(supabase, roundId),
      gameService.getRoundLifelines(supabase, roundId),
    ]);

    return { round, questions, lifelines };
  },

  /**
   * Get a bonus round with all its child data (answers, responses, or questions).
   */
  async getBonusRoundWithDetails(
    supabase: DozenSupabaseClient,
    bonusRoundId: string,
  ): Promise<{
    bonusRound: BonusRoundRow;
    classicAnswers: ClassicBonusAnswerRow[];
    classicTeamResponses: ClassicBonusTeamResponseRow[];
    closestGuessQuestions: ClosestGuessQuestionRow[];
  } | null> {
    const bonusRound = await gameService.getBonusRound(
      supabase,
      bonusRoundId,
    );
    if (!bonusRound) return null;

    const [classicAnswers, classicTeamResponses, closestGuessQuestions] =
      await Promise.all([
        gameService.getClassicBonusAnswers(supabase, bonusRoundId),
        gameService.getClassicBonusTeamResponses(supabase, bonusRoundId),
        gameService.getClosestGuessQuestions(supabase, bonusRoundId),
      ]);

    return {
      bonusRound,
      classicAnswers,
      classicTeamResponses,
      closestGuessQuestions,
    };
  },

  // ==========================================================================
  // QUESTION ANSWERS — Track which players answered each question correctly
  // ==========================================================================

  /** Get all active (non-deleted) answer records for a question. */
  async getQuestionAnswers(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<QuestionAnswerRow[]> {
    const { data, error } = await supabase
      .from("question_answers")
      .select("*")
      .eq("question_id", questionId)
      .is("deleted_at", null);

    if (error) {
      console.error(
        `Error fetching answers for question ${questionId}:`,
        error,
      );
      throw error;
    }

    return data || [];
  },

  /** Get all active answer records for multiple questions (batch). */
  async getQuestionAnswersByQuestionIds(
    supabase: DozenSupabaseClient,
    questionIds: string[],
  ): Promise<QuestionAnswerRow[]> {
    if (questionIds.length === 0) return [];

    const { data, error } = await supabase
      .from("question_answers")
      .select("*")
      .in("question_id", questionIds)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching answers for questions:", error);
      throw error;
    }

    return data || [];
  },

  /** Add a player as a correct answerer for a question. */
  async addQuestionAnswer(
    supabase: DozenSupabaseClient,
    questionId: string,
    profileId: string,
  ): Promise<QuestionAnswerRow> {
    const { data, error } = await supabase
      .from("question_answers")
      .insert({ question_id: questionId, profile_id: profileId })
      .select()
      .single();

    if (error) {
      console.error(
        `Error adding answer for question ${questionId}, profile ${profileId}:`,
        error,
      );
      throw error;
    }

    return data;
  },

  /** Soft-delete a player's answer for a question. */
  async removeQuestionAnswer(
    supabase: DozenSupabaseClient,
    questionId: string,
    profileId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("question_answers")
      .update({ deleted_at: new Date().toISOString() })
      .eq("question_id", questionId)
      .eq("profile_id", profileId)
      .is("deleted_at", null);

    if (error) {
      console.error(
        `Error removing answer for question ${questionId}, profile ${profileId}:`,
        error,
      );
      throw error;
    }
  },

  /** Soft-delete all answers for a question. */
  async clearQuestionAnswers(
    supabase: DozenSupabaseClient,
    questionId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("question_answers")
      .update({ deleted_at: new Date().toISOString() })
      .eq("question_id", questionId)
      .is("deleted_at", null);

    if (error) {
      console.error(
        `Error clearing answers for question ${questionId}:`,
        error,
      );
      throw error;
    }
  },
};
